import React from 'react';
import { Dialog, IconButton, DialogContent, DialogActions, DialogTitle, TextField } from '@mui/material';
import { Button } from 'components/index';
import CloseIcon from '@mui/icons-material/Close';
import { InputCCReportDTO } from './construction-cost-report.type';
import { useFormik } from 'formik';
import * as yup from 'yup';
import NumericFormatInput from '../general/NumericFormatInput';

interface InputCCReportDialogProps {
  open: boolean;
  onClose: () => void;
  initialValues?: InputCCReportDTO;
  setValues: (values: InputCCReportDTO) => void;
  loading?: boolean;
}

const InputCCReportDialog: React.FC<InputCCReportDialogProps> = ({ open, onClose, initialValues, setValues, loading }) => {
  const initialOrDefaultValues: InputCCReportDTO = {
    name: initialValues?.name ?? '',
    grossBuildingSF: initialValues?.grossBuildingSF,
    netRentableSF: initialValues?.netRentableSF,
    unitCount: initialValues?.unitCount,
  };

  const formik = useFormik({
    initialValues: initialOrDefaultValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      name: yup.string().required('Required'),
      grossBuildingSF: yup.number().nullable().min(0, 'Must not be negative'),
      netRentableSF: yup.number().nullable().min(0, 'Must not be negative'),
      unitCount: yup.number().nullable().min(0, 'Must not be negative'),
    }),
    onSubmit: values => {
      setValues(values);
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Enter Report Data</DialogTitle>
      <IconButton onClick={onClose} style={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ paddingTop: 0 }}>
        <form onSubmit={formik.handleSubmit}>
          <TextField
            fullWidth
            margin='normal'
            label='Name'
            name='name'
            required
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
          <TextField
            fullWidth
            margin='normal'
            label='Gross Building SF (for Projections)'
            name='grossBuildingSF'
            value={formik.values.grossBuildingSF}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.grossBuildingSF && Boolean(formik.errors.grossBuildingSF)}
            helperText={formik.touched.grossBuildingSF && formik.errors.grossBuildingSF}
            InputProps={{
              inputComponent: NumericFormatInput as any,
            }}
          />
          <TextField
            fullWidth
            margin='normal'
            label='Net Rentable SF (for Projections)'
            name='netRentableSF'
            value={formik.values.netRentableSF}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.netRentableSF && Boolean(formik.errors.netRentableSF)}
            helperText={formik.touched.netRentableSF && formik.errors.netRentableSF}
            InputProps={{
              inputComponent: NumericFormatInput as any,
            }}
          />
          <TextField
            fullWidth
            margin='normal'
            label='Unit Count (for Projections)'
            name='unitCount'
            value={formik.values.unitCount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.unitCount && Boolean(formik.errors.unitCount)}
            helperText={formik.touched.unitCount && formik.errors.unitCount}
            InputProps={{
              inputComponent: NumericFormatInput as any,
            }}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='error' onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={formik.handleSubmit}
          disabled={!formik.dirty || !formik.isValid}
          loading={formik.isSubmitting || loading}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InputCCReportDialog;
